'use client';

import { m } from 'motion/react';
import { useState } from 'react';
import { containerVariants, itemVariants, viewPort } from 'utils/framer';
import { highlightText } from './privatleasing/TextBox';
import { VideoClip } from './rentAndShare/VideoClip';
import CloseIcon from './ui/CloseIcon';
import LinkButton from './ui/LinkButton';
import Modal from './ui/Modal';
import PlayVideoIcon from './ui/PlayVideoIcon';
import KIAImage from './ui/image/KIAImage';

export type ContentMedia = {
  title?: string;
  text?: string;
  boldText?: string[];
  link?: {
    url: string;
    text: string;
  };
  layout?: 'col' | 'row';
  video?: {
    src: string;
    srcWebm?: string;
  };
  videoMobile?: {
    src: string;
    srcWebm?: string;
  };
  thumbnail?: {
    src: string;
    alt: string;
  };
};

type ContentVideo = {
  video?: {
    src: string;
    srcWebm?: string;
  };
  videoMobile?: {
    src: string;
    srcWebm?: string;
  };
};

type ContentMediaListProps = {
  items: ContentMedia[];
};

const ContentMediaList = ({ items }: ContentMediaListProps) => {
  const [currentVideoPlaying, setCurrentVideoPlaying] =
    useState<ContentVideo | null>(null);
  return (
    <>
      <m.ul
        initial="hidden"
        whileInView="visible"
        viewport={viewPort}
        variants={containerVariants}
        className="grid lg:grid-cols-2 gap-y-14 lg:gap-y-12 gap-12"
      >
        {items.map((item, index) => {
          const hasMobileVideo =
            (item.videoMobile && item.videoMobile.src) ||
            (item.videoMobile && item.videoMobile.srcWebm);
          return (
            <m.li
              data-layout={item.layout}
              key={index}
              viewport={viewPort}
              variants={itemVariants}
              className="grid grid-cols-1 lg:grid-cols-2 gap-x-12 data-[layout='row']:lg:col-span-2 data-[layout='col']:lg:grid-cols-1 data-[layout='col']:lg:self-start group"
            >
              <div
                className="flex w-full aspect-video relative "
                onClick={() =>
                  hasMobileVideo
                    ? setCurrentVideoPlaying({
                        video: item.video,
                        videoMobile: item.videoMobile,
                      })
                    : setCurrentVideoPlaying({
                        video: item.video,
                      })
                }
              >
                {item.thumbnail ? (
                  <>
                    <PlayVideoIcon className="w-16 h-16 text-white absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" />
                    <KIAImage
                      className="w-full h-full object-cover"
                      src={item.thumbnail.src}
                      alt={item.thumbnail.alt}
                    />
                  </>
                ) : (
                  <>
                    <PlayVideoIcon className="w-16 h-16 text-white absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" />
                    <video
                      className={`w-full h-full object-cover ${item.videoMobile && (item.videoMobile.src || item.videoMobile.srcWebm) ? 'max-lg:hidden' : ''}`}
                      muted
                      playsInline
                      preload="metadata"
                    >
                      {item.video.srcWebm && (
                        <source
                          src={`${item.video.srcWebm}#t=0.001`}
                          type="video/webm"
                        />
                      )}
                      <source
                        src={`${item.video.src}#t=0.001`}
                        type="video/mp4"
                      />
                    </video>
                    {item.videoMobile &&
                    (item.videoMobile.src || item.videoMobile.srcWebm) ? (
                      <video
                        className="w-full h-full object-cover lg:hidden"
                        muted
                        playsInline
                        preload="metadata"
                      >
                        {item.videoMobile.srcWebm && (
                          <source
                            src={`${item.videoMobile.srcWebm}#t=0.001`}
                            type="video/webm"
                          />
                        )}
                        <source
                          src={`${item.videoMobile.src}#t=0.001`}
                          type="video/mp4"
                        />
                      </video>
                    ) : null}
                  </>
                )}
              </div>
              <div className="flex flex-col gap-1 lg:gap-4 mt-4 lg:mt-0 px-4 lg:px-0 group-data-[layout='col']:lg:pt-4 group-data-[layout='row']:lg:self-center">
                <h3 className="text-[28px] lg:text-[36px] leading-[40px] font-semibold ">
                  {item.title}
                </h3>
                <p className="font-light">
                  {highlightText(item.text, item.boldText)}
                </p>
                {item.link ? (
                  <LinkButton
                    url={item.link.url}
                    text={item.link.text}
                    className="mt-4 lg:mt-2"
                  />
                ) : null}
              </div>
            </m.li>
          );
        })}
      </m.ul>
      {currentVideoPlaying ? (
        <Modal>
          <div className="flex justify-center items-center w-full h-full">
            <button
              aria-label="Close window"
              className="absolute top-6 right-6 flex items-center justify-center w-10 h-10 hover:bg-black/10 opacity-60 hover:opacity-100 rounded-full transition-all z-10"
              onClick={() => setCurrentVideoPlaying(null)}
            >
              <CloseIcon className="w-6 h-6 text-white" />
            </button>
            <VideoClip
              controls
              autoplay
              cropMobile={false}
              videoClasses="w-full max-h-[90vh] lg:aspect-video h-full"
              wrapperClasses={
                currentVideoPlaying.videoMobile ? 'max-lg:hidden' : ''
              }
              {...currentVideoPlaying.video}
            />
            {currentVideoPlaying.videoMobile && (
              <VideoClip
                controls
                autoplay
                cropMobile={false}
                videoClasses="w-full h-full"
                wrapperClasses="lg:hidden"
                {...currentVideoPlaying.videoMobile}
              />
            )}
          </div>
        </Modal>
      ) : null}
    </>
  );
};

export default ContentMediaList;
